// src/services/Integrations/GeneraliIntegration.ts

import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";

const BASE = `${CALCULATOR_BASE_URL}/generali`;

const post = async <T>(jwt: string, path: string, body: object): Promise<T> => {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Generali business errors are HTTP 400 { Errors: [{Message}], IsValid: false }
    let json: any;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = undefined; // not JSON - fall through to generic message
      }
    }
    if (json?.IsValid === false && Array.isArray(json.Errors)) {
      throw new Error(json.Errors.map((e: any) => e.Message).join("; "));
    }
    throw new Error(`Дженерали ${path}: HTTP ${res.status}${text ? ` - ${text}` : ""}`);
  }
  return res.json();
};

const get = async <T>(jwt: string, path: string, params?: Record<string, string>): Promise<T> => {
  const url = new URL(`${BASE}${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${jwt}` } });
  if (!res.ok) throw new Error(`Дженерали ${path}: HTTP ${res.status}`);
  return res.json();
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GeneraliOfferRequest {
  VehiclePurpose: string;       // "1" = Лична употреба, "3" = Такси, etc.
  IsRightWheelDrive: boolean;
  IsInsurerForeigner: boolean;
  InsurerType: string;          // "1" = Physical, "2" = Legal
  Pin: string;
  CommencingDate: string;       // "YYYY-MM-DDTHH:MM:SS"
  RegistrationPlate: string;
  CertificateNumber: string;
  InstallmentsCount: number;
}

export interface GeneraliOfferDetail {
  OfferId?: string;
  TotalDueAmount: number;
  InstallmentAmounts?: {
    Number: number;
    Amount: number;
    Tax: number;
    TotalAmount: number;
    Date: string;
  }[];
  InstallmentsCount?: number;
  Currency?: string;
}

export interface GeneraliOfferResponse {
  OfferDetails: GeneraliOfferDetail[];
}

export interface GeneraliIssueRequest {
  OfferId: string;
  [key: string]: unknown;
}

export interface GeneraliIssueResponse {
  PolicyNumber?: string;
  TransactionId?: string;
  [key: string]: unknown;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const GeneraliIntegrationService = {
  /** Calculate MTPL premium offer */
  getOffer: (jwt: string, payload: GeneraliOfferRequest): Promise<GeneraliOfferResponse> =>
    post(jwt, "/api/policy/mtpl/offer", payload),

  /** Issue policy from offer */
  issuePolicy: (jwt: string, payload: GeneraliIssueRequest): Promise<GeneraliIssueResponse> =>
    post(jwt, "/api/policy/mtpl/issue", payload),

  /** Get payment plan for an offer */
  getPaymentPlan: (jwt: string, offerId: string): Promise<any> =>
    get(jwt, "/api/Payment/PaymentPlan", { offerId }),

  /** Download policy PDF - returns Blob */
  printTransaction: async (jwt: string, transactionId: string): Promise<Blob> => {
    const res = await fetch(`${BASE}/Services/api/Print/Transaction/${transactionId}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) throw new Error(`Дженерали печат: HTTP ${res.status}`);
    return res.blob();
  },

  /** Vehicle purpose nomenclature */
  getVehiclePurposes: (jwt: string): Promise<any> =>
    get(jwt, "/api/Nomenclature/VehiclePurposes"),
};
